import React, { Component } from 'react';
import { getAuthHeaders, urlMap } from '../../../utils';
import ChapterHistoryDiffModal from './ChapterHistoryDiffModal';
import { Icon } from '../../components/Icon';

function parseCommitInfo(message) {
  // Format: chapter:{id} — {chapterTitle} — auto snapshot — {timestamp}
  //      or chapter:{id} — {chapterTitle} — {manualLabel}
  //      or chapter:{id} — auto snapshot — {timestamp}  (no title fallback)
  const parts = message.split(' — ');
  if (parts.length < 2) return { label: message, isAuto: false };
  // parts[1] could be chapterTitle or "auto snapshot" (no-title fallback)
  const part1 = parts[1].trim().toLowerCase();
  const part2 = parts[2]?.trim().toLowerCase() ?? '';
  const isAuto = part1.startsWith('auto snapshot') || part2.startsWith('auto snapshot');
  const label = isAuto ? 'Auto snapshot' : (parts[2]?.trim() || parts[1].trim());
  return { label, isAuto };
}

function groupByDate(commits) {
  const groups = [];
  let lastKey = null;
  for (const commit of commits) {
    const d = new Date(commit.timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    let key;
    if (d.toDateString() === today.toDateString()) key = 'Today';
    else if (d.toDateString() === yesterday.toDateString()) key = 'Yesterday';
    else key = d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

    if (key !== lastKey) {
      groups.push({ label: key, commits: [] });
      lastKey = key;
    }
    groups[groups.length - 1].commits.push(commit);
  }
  return groups;
}

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export default class ChapterHistoryPanel extends Component {
  state = {
    commits: [],
    loading: true,
    error: null,
    diffOpen: false,
    diffCommit: null,
    snapshotContent: null,
    currentFetchedContent: null,
    snapshotLoading: false,
    snapshotError: null,
  };

  componentDidMount() { this.fetchHistory(); }

  componentDidUpdate(prevProps) {
    if (prevProps.chapterId !== this.props.chapterId) {
      this.setState({ commits: [], loading: true, error: null });
      this.fetchHistory();
    }
  }

  fetchHistory = async () => {
    const { chapterId } = this.props;
    if (!chapterId) return;
    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(urlMap.getChapterHistory(chapterId), {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      this.setState({ commits: data.commits ?? [], loading: false });
    } catch (err) {
      this.setState({ error: err.message || 'Failed to load history', loading: false });
    }
  };

  openDiff = async (commit) => {
    this.setState({ diffOpen: true, diffCommit: commit, snapshotContent: null, currentFetchedContent: null, snapshotLoading: true, snapshotError: null });
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const [snapRes, curRes] = await Promise.all([
        fetch(urlMap.getChapterSnapshot(this.props.chapterId, commit.hash), { headers }),
        fetch(urlMap.getChapter(this.props.chapterId), { headers }),
      ]);
      if (!snapRes.ok) throw new Error(`HTTP ${snapRes.status}`);
      if (!curRes.ok) throw new Error(`HTTP ${curRes.status}`);
      const [snapData, curData] = await Promise.all([snapRes.json(), curRes.json()]);
      this.setState({
        snapshotContent: snapData.content ?? '',
        currentFetchedContent: curData.content ?? '',
        snapshotLoading: false,
      });
    } catch (err) {
      this.setState({ snapshotError: err.message || 'Failed to load snapshot', snapshotLoading: false });
    }
  };

  closeDiff = () => this.setState({ diffOpen: false, diffCommit: null, snapshotContent: null, currentFetchedContent: null, snapshotError: null });

  render() {
    const { onClose, currentContent, onCommitToHistory, committingToHistory } = this.props;
    const { commits, loading, error, diffOpen, diffCommit, snapshotContent, currentFetchedContent, snapshotLoading, snapshotError } = this.state;
    const groups = groupByDate(commits);

    return (
      <>
        <div className="step-results-scroll">
          <aside className="step-results-side-panel" aria-label="Writing history panel">
            <div className="step-results-panel-header">
              <span className="step-results-panel-title">Writing history</span>
              <div className="summary-side-actions">
                <button
                  type="button"
                  className="summary-modal-reload-btn"
                  onClick={this.fetchHistory}
                  title="Refresh history"
                  aria-label="Refresh history"
                  disabled={loading}
                >
                  <Icon.Restart />
                </button>
                <button
                  type="button"
                  className={`btn subtle chapter-commit-btn${committingToHistory ? ' is-loading' : ''}`}
                  onClick={onCommitToHistory}
                  disabled={committingToHistory}
                  title="Save a snapshot to writing history"
                  aria-label="Commit to history"
                >
                  {committingToHistory ? 'Committing…' : 'Commit'}
                </button>
                <button className="btn subtle" onClick={onClose} title="Close panel" aria-label="Close history panel">
                  <Icon.Close />
                </button>
              </div>
            </div>

            <div className="step-results-panel-body">
              {loading && (
                <div className="summary-modal-loading">
                  <div className="summary-modal-loading-spinner" />
                  <div>Loading history…</div>
                </div>
              )}

              {!loading && error && (
                <div className="summary-modal-error">
                  <p>{error}</p>
                  <button type="button" className="btn primary" onClick={this.fetchHistory} style={{ marginTop: 12 }}>
                    Retry
                  </button>
                </div>
              )}

              {!loading && !error && commits.length === 0 && (
                <div className="summary-modal-empty">
                  <p>No snapshots yet.</p>
                  <p style={{ marginTop: 8, fontSize: 13, color: 'var(--text-3)' }}>
                    Click <strong>Commit</strong> to save the first snapshot, or keep writing — one will be created automatically.
                  </p>
                </div>
              )}

              {!loading && !error && groups.map((group) => (
                <div key={group.label} className="history-group">
                  <div className="history-group-label">{group.label}</div>
                  <ul className="history-entry-list" role="list">
                    {group.commits.map((commit) => {
                      const { label, isAuto } = parseCommitInfo(commit.message);
                      return (
                      <li key={commit.hash}>
                        <div className="issue-card">
                          <div
                            className="issue-card-header"
                            role="button"
                            tabIndex={0}
                            onClick={() => this.openDiff(commit)}
                            onKeyDown={(e) => e.key === 'Enter' && this.openDiff(commit)}
                            title="View diff"
                          >
                            <span className="issue-card-severity-dot" style={{ background: isAuto ? 'var(--text-3)' : 'var(--green)' }} aria-hidden />
                            <span className="issue-card-title">{label}</span>
                            <span className="issue-card-scope">{formatTime(commit.timestamp)}</span>
                            <span className="issue-card-scope" style={{ fontFamily: 'monospace', fontSize: 11 }}>{commit.shortHash}</span>
                            <span className="issue-card-chevron" aria-hidden>›</span>
                          </div>
                        </div>
                      </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </aside>
        </div>

        <ChapterHistoryDiffModal
          isOpen={diffOpen}
          onClose={this.closeDiff}
          currentContent={snapshotLoading ? null : (currentFetchedContent ?? currentContent)}
          snapshotContent={snapshotLoading ? null : (snapshotContent ?? '')}
          commitInfo={diffCommit}
          snapshotLoading={snapshotLoading}
          snapshotError={snapshotError}
        />
      </>
    );
  }
}


function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
